import { ContentType as ForgeContentType } from "@amzn/ads-forge-client";
import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { useAsync } from "@react-hookz/web";
import { startOfHour } from "date-fns";
import { isString } from "lodash";
import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { useStore } from "zustand";

import { getBlinkApiClientV2 } from "../../api/blinkApi/blinkApi";
import { COPY_MESSAGES, TOOLTIP_MESSAGES } from "../../constants/common";
import { G4sPlacement } from "../../constants/g4sPlacement";
import { TELEMETRY_ID, PHOENIX_FEATURE } from "../../constants/telemetry";
import { useSetInsightDetailComponents } from "../../hooks/insightDetail/useSetInsightDetailComponents";
import { FeatureFlagsStore } from "../../store/featureFlags";
import { globalWorkbenchStore } from "../../store/globalWorkbench";
import { insightDetailComponentStore } from "../../store/insightDetailComponent";
import { notificationStore, NotificationType } from "../../store/notification";
import { InsightPreviewFileFormats } from "../../types/insightPreviewFileFormats";
import {
    generateSharableLinkId,
    generateShareableUrl,
} from "../../utils/api.utils";
import { copyToClipboard } from "../../utils/clipboard.utils";
import type {
    InsightDetailOption,
    InsightDetailSelectionProps,
} from "../../utils/insightDetail.utils";
import { InsightDetailType } from "../../utils/insightDetail.utils";
import { getAdvertiserId } from "../../utils/permission.utils";
import {
    getFilterTelemetryDetails,
    getUnifiedFilterTelemetryDetails,
    logWithTakt,
} from "../../utils/telemetry.utils";
import { ShareIcon } from "../common/ShareIcon";
import {
    BodyComponentBuilder,
    FooterComponentBuilder,
    Header,
    HeaderCardDetails,
    HeaderComponentBuilder,
    HeaderTitle,
    IcmBusinesslineConfiguration,
} from "../common/insightDetail.components";
import {
    AddedToGlobalWorkbenchNotification,
    GlobalWorkbenchCartActionErrorNotification,
    UpdateGlobalWorkbenchNotification,
} from "../common/notification.components";
import { DeckDecorationWithLanguageModalButton } from "../createDeckDecoration/DeckDecorationWithLanguageModalButton";
import type { InsightFamilyDetails } from "../discovery/insights/insightFamilies/useSetInsightFamilyDetails";
import {
    ForgeNarrativeDownloadButton,
    ForgeNarrativeSelector,
    ForgeNarrativeTranslateModalButton,
    getForgeApiMFCClient,
    InsightWorkbenchCartButton,
} from "../remote";
import { ForgeNarrativeLoader } from "../remote/ForgeNarrativeLoader";

import { ForgeBetaOptInToggle } from "./forgeBetaOptInToggle";

const StyledNarrativeSelectorContainer = styled.div``;

export const useSetForgeMFCInsightFamilyDetails = (
    props: InsightFamilyDetails,
): (() => void) => {
    const {
        identifier,
        familyName,
        details,
        insightType,
        title,
        templateInfo,
        downloadLoading,
        insightFamilyTrackingId,
        businesslineId,
        icmVersion,
        reset,
        handleForgeBetaToggle,
    } = props;

    const narrativeSelectorContainerRef = React.useRef<HTMLDivElement>(null);

    const [resetStore] = useStore(insightDetailComponentStore, (state) => [
        state.reset,
    ]);
    const [areComponentsReady, setComponentsReady] = useState(false);

    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);

    const isWorkbenchForgeEnabled = useStore(
        FeatureFlagsStore,
        (state) => state.workbenchForge,
    );

    const [getSlidesSelected, insightInCart] = useStore(
        globalWorkbenchStore,
        (state) => [state.getSlidesSelected, state.isInsightInCart(identifier)],
    );

    const [addedToCart, setAddedToCart] = useState(false);
    const [forgeOutput, setForgeOutput] = useState<any>();
    const [forgeLastChanged, setForgeLastChanged] = useState(
        new Date().toISOString(),
    );

    const [numPages, setNumPages] = React.useState<number>(0);
    const handleMount = (props: InsightDetailSelectionProps): void => {
        props.setSelectedSlides(new Set(getSlidesSelected(identifier)));
    };

    const handleClose = () => {
        setNumPages(0);
        setAddedToCart(false);
        setComponentsReady(false); // Reset component ready state to ensure re-rendering on reopen
        resetStore();
        reset();
    };

    const InsightFamilyHeaderOption: InsightDetailOption = useMemo(() => {
        const handleShareClick = async () => {
            const baseUrl = window.location.origin + window.location.pathname;

            if (!identifier) {
                return;
            }

            const sharableIdentifier = await generateSharableLinkId(
                identifier,
                insightType,
            );

            const urlParams: Record<string, string> = {
                identifier: sharableIdentifier,
            };

            const searchParams = new URLSearchParams(window.location.search);
            const currentFilter = searchParams.get("filters");
            const sfId = searchParams.get("sfId");

            if (sfId) {
                urlParams.sfId = sfId;
            }

            if (currentFilter) {
                urlParams.filters = currentFilter;
            }

            const shareUrl = generateShareableUrl(baseUrl, urlParams);
            copyToClipboard(shareUrl, "share-url", COPY_MESSAGES.INSIGHT);
            const filters = useUnifiedSearch
                ? getUnifiedFilterTelemetryDetails()
                : getFilterTelemetryDetails();

            logWithTakt(TELEMETRY_ID.INSIGHT_SHARE_SUCCESS, {
                ...getFilterTelemetryDetails(),
                identifier,
                shareType: "insightFamily",
            });
            PhoenixMetrics.emit(
                TELEMETRY_ID.INSIGHT_SHARE_SUCCESS,
                PHOENIX_FEATURE.FORGE,
                {
                    contentInfo: identifier
                        ? {
                              contentProvider: insightType ?? "",
                              contentType: "",
                              contentId: identifier,
                          }
                        : undefined,
                    customData: {
                        filters: getFilterTelemetryDetails(),
                        shareType: "insightFamily",
                    },
                },
            );
        };

        const InsightFamilyHeader: React.FunctionComponent<
            InsightDetailSelectionProps
        > = (props: InsightDetailSelectionProps) => (
            <HeaderComponentBuilder
                loading={downloadLoading}
                headerComponents={[
                    <Header
                        numPages={numPages}
                        identifier={identifier}
                        insightType={insightType}
                    >
                        <ShareIcon
                            onClick={() => void handleShareClick()}
                            tooltipMessage={TOOLTIP_MESSAGES.INSIGHT}
                            testId="insight-family-share-icon"
                        />
                    </Header>,
                    <HeaderTitle title={title} />,
                    <HeaderCardDetails
                        details={details}
                        templateInfo={templateInfo}
                        selectedSlides={props.selectedSlides}
                        setSelectedSlides={props.setSelectedSlides}
                        numPages={numPages}
                        previewFileFormat={InsightPreviewFileFormats.FORGE}
                        identifier={identifier}
                        insightType={insightType ?? InsightType.INSIGHT}
                        buttonOverride={[
                            <ForgeNarrativeTranslateModalButton
                                contentIdentifier={identifier}
                            />,
                            <DeckDecorationWithLanguageModalButton
                                identifier={identifier}
                                insightType={insightType ?? InsightType.INSIGHT}
                                documentUrlAsync={getDocumentUrlAsync}
                                placement={G4sPlacement.SIDE_PANEL}
                            />,
                        ]}
                    />,
                    businesslineId && icmVersion ? (
                        <IcmBusinesslineConfiguration
                            businesslineId={businesslineId}
                            icmVersion={icmVersion}
                        />
                    ) : (
                        <></>
                    ),
                ]}
            />
        );
        return {
            type: InsightDetailType.SELECTION,
            component: InsightFamilyHeader,
        };
    }, [identifier, numPages, details, templateInfo, downloadLoading]);

    const _handleForgeBetaToggle = () => {
        handleForgeBetaToggle?.();
        handleClose();
    };

    const InsightFamilyBodyOption: InsightDetailOption = useMemo(() => {
        const InsightFamilyBody: React.FunctionComponent<
            InsightDetailSelectionProps
        > = (props: InsightDetailSelectionProps) => {
            const [workbenchSourceAsset, getWorkbenchSourceAsset] = useAsync(
                async () => {
                    const { getForgeWorkbenchSourceAsset } = await import(
                        "adsMagnetoMfc/Api"
                    );
                    return await getForgeWorkbenchSourceAsset(
                        getAdvertiserId(),
                        identifier,
                    );
                },
            );

            React.useEffect(
                () => void getWorkbenchSourceAsset.execute(),
                [forgeLastChanged],
            );
            return (
                <BodyComponentBuilder
                    loading={workbenchSourceAsset.status === "loading"} // Loading rendering skeleton is handled by the Narrative component
                    bodyComponents={[
                        ...(handleForgeBetaToggle
                            ? [
                                  <ForgeBetaOptInToggle
                                      onToggle={_handleForgeBetaToggle}
                                  />,
                              ]
                            : []),
                        <StyledNarrativeSelectorContainer
                            ref={narrativeSelectorContainerRef}
                        >
                            {isWorkbenchForgeEnabled ? (
                                <>
                                    {insightInCart ? (
                                        <>
                                            {workbenchSourceAsset.status ===
                                            "success" ? (
                                                <ForgeNarrativeLoader
                                                    allowSelection={
                                                        isWorkbenchForgeEnabled
                                                    }
                                                    selectedSlides={
                                                        props.selectedSlides
                                                    }
                                                    onUpdateSelectedSlides={(
                                                        e,
                                                    ) => {
                                                        props.setSelectedSlides(
                                                            e,
                                                        );
                                                    }}
                                                    contentIdentifier={
                                                        identifier
                                                    }
                                                    contentType={
                                                        ForgeContentType.INSIGHT
                                                    }
                                                    baseNarrative={
                                                        workbenchSourceAsset
                                                            .result.narrative
                                                    }
                                                    currentNarrative={
                                                        workbenchSourceAsset
                                                            .result.narrative
                                                    }
                                                    chartCustomizations={
                                                        workbenchSourceAsset
                                                            .result
                                                            .chartCustomizations
                                                    }
                                                    setNumPages={setNumPages}
                                                />
                                            ) : (
                                                <div></div>
                                            )}
                                        </>
                                    ) : addedToCart ? (
                                        <ForgeNarrativeLoader
                                            allowSelection={
                                                isWorkbenchForgeEnabled
                                            }
                                            selectedSlides={
                                                props.selectedSlides
                                            }
                                            onUpdateSelectedSlides={(e) => {
                                                props.setSelectedSlides(e);
                                            }}
                                            contentIdentifier={identifier}
                                            contentType={
                                                ForgeContentType.INSIGHT
                                            }
                                            baseNarrative={
                                                forgeOutput.narrative
                                            }
                                            currentNarrative={
                                                forgeOutput.narrative
                                            }
                                            chartCustomizations={
                                                forgeOutput.chartCustomizations
                                            }
                                        />
                                    ) : (
                                        <ForgeNarrativeLoader
                                            allowSelection={
                                                isWorkbenchForgeEnabled
                                            }
                                            selectedSlides={
                                                props.selectedSlides
                                            }
                                            onUpdateSelectedSlides={(e) => {
                                                props.setSelectedSlides(e);
                                            }}
                                            contentIdentifier={identifier}
                                            contentType={
                                                ForgeContentType.INSIGHT
                                            }
                                        />
                                    )}
                                </>
                            ) : (
                                <ForgeNarrativeSelector
                                    selectedSlides={props.selectedSlides}
                                    setSelectedSlides={props.setSelectedSlides}
                                    contentIdentifier={identifier}
                                    contentType={ForgeContentType.INSIGHT}
                                    setNumPages={setNumPages}
                                />
                            )}
                        </StyledNarrativeSelectorContainer>,
                    ]}
                />
            );
        };
        return {
            type: InsightDetailType.SELECTION,
            component: InsightFamilyBody,
        };
    }, [
        identifier,
        areComponentsReady,
        forgeLastChanged,
        addedToCart,
        insightInCart,
    ]);

    const getDocumentUrlAsync = React.useCallback(async () => {
        const SUMMARY_ASSET_ERROR_MESSAGE =
            "Failed to create asset used for summary";

        // get clients
        const [forgeClient, blinkClient] = await Promise.all([
            getForgeApiMFCClient(),
            getBlinkApiClientV2(),
        ]);

        const cacheId = startOfHour(new Date()).toISOString();
        const resourceIdentifier = `${identifier}_${cacheId}`;

        try {
            // build forge pptx
            const forgePptxResponse = await forgeClient.getNarrativeAsPptx({
                contentIdentifier: identifier,
                contentType: ForgeContentType.INSIGHT,
            });

            // convert pptx -> pdf
            const forgePdfConversionResponse =
                await blinkClient.createAsyncOfficeConvert({
                    clientId: "IHUB",
                    applicationContext: "forge-summary",
                    format: "pdf",
                    resourceIdentifier,
                    inputLocation: forgePptxResponse.s3Location,
                    outputLocation: {
                        bucket: forgePptxResponse.s3Location?.bucket,
                        key: `${resourceIdentifier}.pdf`,
                    },
                });

            // wait for pdf to finish
            const { outputFileDownloadUrl } =
                await blinkClient.pollGetAsyncOfficeConvert({
                    executionId: forgePdfConversionResponse.execution,
                });

            if (isString(outputFileDownloadUrl)) return outputFileDownloadUrl;

            throw new Error(SUMMARY_ASSET_ERROR_MESSAGE);
        } catch (err) {
            console.error("Failed to create asset used for summary:", err);
            const errorMessage =
                err instanceof Error ? err.message : String(err);
            throw new Error(`${SUMMARY_ASSET_ERROR_MESSAGE}: ${errorMessage}`);
        }
    }, [identifier]);

    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);

    const InsightFamilyFooterOption: InsightDetailOption = useMemo(() => {
        const InsightFamilyFooter: React.FunctionComponent<
            InsightDetailSelectionProps
        > = (props: InsightDetailSelectionProps) => {
            const [forgeStore, setForgeStore] = React.useState<any>(null);
            const [exportForge, setExportForge] = React.useState<any>(null);

            React.useEffect(() => {
                import("adsForgeMfc/ForgeNarrativeStore")
                    .then(({ forgeNarrativesStore }) => {
                        setForgeStore(forgeNarrativesStore);
                    })
                    .catch(() => {});
                import("adsForgeMfc/ExportNarrativeForge")
                    .then(({ exportNarrativeForge }) => {
                        setExportForge(() => exportNarrativeForge);
                    })
                    .catch(() => {});
            }, []);

            const isDirty = useStore(
                forgeStore || {
                    subscribe: () => () => {},
                    getState: () => ({ isNarrativeDirty: () => false }),
                },
                (s: any) => s.isNarrativeDirty(identifier),
            );
            const exportForgeNarrativeForWorkbench = React.useCallback(
                () => exportForge?.(identifier),
                [exportForge, identifier],
            );
            const selectedSlides = props.selectedSlides;

            const insightWorkbenchCartButtonProps = {
                workbenchScope: getAdvertiserId(),
                insightIdentifier: identifier,
                source: {
                    name: title,
                    slidesSelected: [...selectedSlides],
                    assets: {},
                    origin: {
                        clientId: "IHUB",
                        type: insightType ?? InsightType.INSIGHT,
                        subType: familyName,
                    },
                    deckMetadata: {
                        insightIdentifier: identifier,
                        insightType: insightType ?? InsightType.INSIGHT,
                        numPages: numPages ?? 0,
                    },
                    slideMetadata: [],
                },
            };
            return (
                <FooterComponentBuilder
                    loading={false}
                    handleClose={handleClose}
                    identifier={identifier}
                    insightType={insightType}
                    footerComponents={[
                        <ForgeNarrativeDownloadButton
                            contentIdentifier={identifier}
                            contentType={ForgeContentType.INSIGHT}
                        />,
                        isWorkbenchForgeEnabled ? (
                            <InsightWorkbenchCartButton
                                disabled={
                                    selectedSlides.size === 0 &&
                                    !insightInCart &&
                                    !addedToCart
                                }
                                {...insightWorkbenchCartButtonProps}
                                exportForgeNarrative={
                                    exportForgeNarrativeForWorkbench
                                }
                                forgeNarrativeDirty={isDirty}
                                onSuccess={() => {
                                    setAddedToCart(true);
                                    setForgeOutput(
                                        exportForgeNarrativeForWorkbench(),
                                    );
                                    setForgeLastChanged(
                                        new Date().toISOString(),
                                    );
                                    upsertNotification(
                                        <AddedToGlobalWorkbenchNotification
                                            slidesAdded={selectedSlides.size}
                                        />,
                                        NotificationType.INFO,
                                        identifier,
                                    );
                                }}
                                onUpdate={() => {
                                    setAddedToCart(true);
                                    setForgeOutput(
                                        exportForgeNarrativeForWorkbench(),
                                    );
                                    setForgeLastChanged(
                                        new Date().toISOString(),
                                    );
                                    upsertNotification(
                                        <UpdateGlobalWorkbenchNotification />,
                                        NotificationType.INFO,
                                        identifier,
                                    );
                                }}
                                onError={() => {
                                    upsertNotification(
                                        <GlobalWorkbenchCartActionErrorNotification />,
                                        NotificationType.ERROR,
                                        identifier,
                                    );
                                }}
                            />
                        ) : (
                            <></>
                        ),
                    ]}
                />
            );
        };
        return {
            type: InsightDetailType.SELECTION,
            component: InsightFamilyFooter,
        };
    }, [
        insightFamilyTrackingId,
        numPages,
        downloadLoading,
        forgeOutput,
        forgeLastChanged,
        insightInCart,
        addedToCart,
    ]);

    const setInsigtDetailComponents = useSetInsightDetailComponents({
        identifier,
        familyName,
        InsightHeaderOption: InsightFamilyHeaderOption,
        InsightBodyOption: InsightFamilyBodyOption,
        InsightFooterOption: InsightFamilyFooterOption,
        headerReady: areComponentsReady,
        bodyReady: areComponentsReady,
        footerReady: areComponentsReady,
        handleMount,
        handleClose,
    });

    return () => {
        setComponentsReady(true);
        setInsigtDetailComponents();
    };
};
